import { useState } from "react";
import { Collapse } from "react-bootstrap";
import "./FeatureDetail.css";
import { useTohonBuy } from "../context/TohonBuyContext";
import {
  getOutsideBusinessHoursMessage,
  createFeaturePropertyKey,
} from "../utils/Util";
import { useAuthStatus } from "../context/AuthContext";
import { getIsOredrHistoryView, getOrderHistoryMap } from "../utils/MapManager";
import {
  NINI_BETA_NOTICE,
  ABOUT_SECTION_NINI,
  EXCLUDED_PROPERTY_KEYS,
} from "../constants/Constant";

// 謄本選択ボタンのラベル
const CAP_TOUKI_SELECT_BTN_LABEL =
  "<br>不動産登記情報（土地）<br>を購入リストに追加";
// 謄本リスト削除ボタンのラベル
const CAP_TOUKI_REMOVE_BTN_LABEL =
  "<br>不動産登記情報（土地）<br>を購入リストから削除";

/**
 * 地番から文字列を生成する
 */
const createChibanStr = (oaza, chome, koaza, yobi, chiban) => {
  let str = "";
  str += oaza ? oaza : "";
  str += chome ? chome : "";
  str += koaza ? koaza : "";
  str += yobi ? yobi : "";
  str += chiban ? chiban : "";
  return str;
};

// コード定義のマッピングオブジェクト
const BUILDING_CODE_MAP = {
  class: {
    // 建築物の形態による区分　Building_class.xml
    3001: "普通建物",
    3002: "堅ろう建物",
    3003: "普通無壁舎",
    3004: "堅ろう無壁舎",
    3000: "分類しない建物",
  },
  usage: {
    // 建築物の主な使い道（用途）　Building_usage.xml
    401: "業務施設",
    402: "商業施設",
    403: "宿泊施設",
    404: "商業系複合施設",
    411: "住宅",
    412: "共同住宅",
    413: "店舗等併用住宅",
    414: "店舗等併用共同住宅",
    415: "作業所併用住宅",
    421: "官公庁施設",
    422: "文教厚生施設",
    431: "運輸倉庫施設",
    441: "工場",
    451: "農林漁業用施設",
    452: "供給処理施設",
    453: "防衛施設",
    454: "その他",
    461: "不明",
  },
};

/**
 * 建築物のコードリストから表示用の文字列を取得する
 */
const getBuildingCodeListXML = (property, value) => {
  return BUILDING_CODE_MAP[property]?.[value] ?? value;
};

/**
 * 建築物の詳細を表示するコンポーネント
 * @param {{properties: object}} props
 */
const BuildingDetail = ({ properties }) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleToggle = (e) => {
    // クリックされた要素が a タグ、または a タグの子孫要素であれば何もしない
    if (e.target.closest("a")) {
      return;
    }
    setIsOpen(!isOpen);
  };

  const labels = {
    filename: "ファイル名",
    gml_id: "gml_id",
    lod_type: "LOD",
    class: "区分",
    usage: "用途",
    yearOfConstruction: "建築された年",
    storeysAboveGround: "地上階の階数",
    storeysBelowGround: "地下階の階数",
    measuredHeight: "高さ(計測値)m",
  };

  return (
    <li className="item">
      <button
        type="button"
        className={`item__head gsi--info--open_building ${
          isOpen ? "active" : ""
        }`}
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        {getBuildingCodeListXML("class", properties["class"]) || "　"}
      </button>
      <Collapse in={isOpen}>
        <div className="item__dtl">
          <table className="c-tbl ft--S item__dtl__tbl">
            <tbody>
              {Object.entries(properties)
                .filter(([key]) => labels[key])
                .map(([key, value]) => (
                  <tr key={key}>
                    <th>{labels[key]}</th>
                    <td>{getBuildingCodeListXML(key, value) ?? ""}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Collapse>
    </li>
  );
};

/**
 * 土地の詳細を表示するコンポーネント
 */
const NestedTables = ({ data }) => (
  <>
    {data.map((item, itemIndex) => (
      <table key={itemIndex}>
        <tbody>
          {Object.entries(item).map(([key, value], fieldIndex) => (
            <tr
              key={fieldIndex}
              style={fieldIndex === 0 ? { borderTop: "solid 1px #b4b4b4" } : {}}
            >
              <th>{key}</th>
              <td>{value ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    ))}
  </>
);
const LandDetail = ({
  properties,
  isMainMap,
  downloadGeojson,
  openChibanPolygonDl,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleToggle = (e) => {
    // クリックされた要素が a タグ、または a タグの子孫要素であれば何もしない
    if (e.target.closest("a")) {
      return;
    }
    setIsOpen(!isOpen);
  };

  const { userInfo, isUserLoggedIn } = useAuthStatus();
  const {
    tohonBuyList,
    addTohon,
    removeTohon,
    isTohonInList,
    isNotMaxTohonList,
    maxTohonCount,
  } = useTohonBuy();

  const fileName = properties["ファイル名"]?.replace(".xml", "");
  const downloadIcon = (
    <>
      <a
        href="#dummy"
        role="button"
        className="btn btn-link"
        onClick={(e) => {
          e.preventDefault();
          if (isUserLoggedIn) {
            downloadGeojson(fileName);
          } else {
            //ログインを促すモーダルを開く
            window.dispatchEvent(
              new CustomEvent("showLoginPrompt", { detail: { showButton: true } })
            );
          }
        }}
      >
        <i
          className="fa fa-download"
          style={{ color: "#555555", fontSize: "20px" }}
          aria-hidden="true"
        ></i>
        GeoJSON
      </a>
      <a
        href="https://www.youtube.com/watch?v=p34NZVbTC-c"
        data-lity="data-lity"
      >
        <i
          className="fa fa-youtube-play"
          style={{
            color: "#787878",
            fontSize: "23px",
            display: "inline-block",
            verticalAlign: "middle",
          }}
          aria-hidden="true"
        ></i>
      </a>
    </>
  );

  // 地番ポリゴンダウンロードの利用可否（有料サブスクユーザー限定）
  const isEnabledSubscription =
    userInfo?.attributes &&
    userInfo.attributes["custom:subscription_status"] === "active"
      ? true
      : false;

  // 筆アコーディオンヘッダーの地番ポリゴン DL ボタン（実行前チェックは RightSide 側で行う）
  // 無効時は .disabled で pointer-events が無効になるため title はラッパーの span に付ける
  const chibanPolygonDlIcon = openChibanPolygonDl ? (
    <span
      title={
        !isEnabledSubscription
          ? "この機能はサブスクリプションの申し込みが必要です。"
          : ""
      }
    >
      <a
        href="#dummy"
        role="button"
        className={`btn btn-link${isEnabledSubscription ? "" : " disabled"}`}
        onClick={(e) => {
          e.preventDefault();
          if (isEnabledSubscription) {
            openChibanPolygonDl();
          }
        }}
      >
        <i
          className="fa fa-download"
          style={{ color: "#555555", fontSize: "20px" }}
          aria-hidden="true"
        ></i>
        地番ポリゴン
      </a>
    </span>
  ) : null;

  let label;
  if (properties["地番"] === undefined) {
    // fgb 一括DLボタンは属性テーブル内の「ファイル名」行のみに表示する（ヘッダには出さない）
    label = properties["ファイル名"];
  } else if (chibanPolygonDlIcon) {
    label = (
      <>
        {properties["地番"]} {chibanPolygonDlIcon}
      </>
    );
  } else {
    label = properties["地番"];
  }

  // 購入日時
  let orderDateHtml = null;
  if (getIsOredrHistoryView()) {
    const featureKey = createFeaturePropertyKey(properties);
    const item = getOrderHistoryMap().get(featureKey);
    if (item && item.length > 0) {
      orderDateHtml = (
        <div className="order-date py-1">
          <div className="order-date-label">
            <b>購入日時</b>
          </div>
          <ul className="p-0">
            {item.map((history, index) => (
              <li key={index}>{history.order_dt}</li>
            ))}
          </ul>
        </div>
      );
    }
  }

  // 謄本購入ボタン関連
  let tohonButtonHtml = null;
  let openBuyModalButtonHtml = null;
  const chiban = properties["地番"];
  const regexp = /水|道|W|V|X|Y|筆界未定地|長狭物不明|無地番/;

  if (chiban !== undefined && !regexp.test(chiban)) {
    const oaza = properties["大字名"] ?? "";
    const chome = properties["丁目名"] ?? "";
    const koaza = properties["小字名"] ?? "";
    const yobi = properties["予備名"] ?? "";
    const code = properties["市区町村コード"];
    const city = properties["市区町村名"];

    const chibanStr = createChibanStr(oaza, chome, koaza, yobi, chiban);
    const tohonItem = { code, city, oaza, chome, koaza, yobi, chiban };
    const isUnique = !isTohonInList(tohonItem);
    const businessHoursMessage = getOutsideBusinessHoursMessage();

    const handleAddClick = () => {
      if (!isUserLoggedIn) {
        window.dispatchEvent(
          new CustomEvent("showLoginPrompt", {
            detail: { showButton: isMainMap },
          })
        );
        return;
      }
      addTohon(tohonItem);
    };

    const handleRemoveClick = () => {
      if (!isUserLoggedIn) {
        window.dispatchEvent(
          new CustomEvent("showLoginPrompt", {
            detail: { showButton: isMainMap },
          })
        );
        return;
      }
      removeTohon(tohonItem);
    };

    if (isUnique) {
      if (businessHoursMessage) {
        tohonButtonHtml = (
          <button
            type="button"
            className="btn btn-success flex-fill"
            disabled
            style={{
              marginTop: "10px",
              marginRight: "10px",
              minHeight: "86px",
            }}
          >
            {businessHoursMessage}
          </button>
        );
      } else if (isNotMaxTohonList()) {
        tohonButtonHtml = (
          <button
            type="button"
            className="btn btn-success flex-fill class-add-tohonlist-button"
            style={{
              marginTop: "10px",
              marginRight: "10px",
              minHeight: "86px",
            }}
            onClick={handleAddClick}
            dangerouslySetInnerHTML={{
              __html: chibanStr + CAP_TOUKI_SELECT_BTN_LABEL,
            }}
          ></button>
        );
      } else {
        tohonButtonHtml = (
          <button
            type="button"
            className="btn btn-success flex-fill class-add-tohonlist-button"
            disabled
            style={{
              marginTop: "10px",
              marginRight: "10px",
              minHeight: "86px",
            }}
          >
            1回の購入は{maxTohonCount}件までです
            <br />
            購入リストから地番を削除してください
          </button>
        );
      }
    } else {
      tohonButtonHtml = (
        <button
          type="button"
          className="btn btn-secondary flex-fill class_removetohonlistbutton"
          style={{
            marginTop: "10px",
            marginRight: "10px",
            minHeight: "86px",
          }}
          onClick={handleRemoveClick}
          dangerouslySetInnerHTML={{
            __html: chibanStr + CAP_TOUKI_REMOVE_BTN_LABEL,
          }}
        ></button>
      );
    }

    // 購入画面を開くボタン
    if (!businessHoursMessage) {
      const handleOpenBuyModal = () => {
        if (isUserLoggedIn) {
          if (userInfo?.attributes?.email && userInfo?.attributes?.sub) {
            window.dispatchEvent(new CustomEvent("showBuyRegistryModal"));
          }
        } else {
          window.dispatchEvent(
            new CustomEvent("showLoginPrompt", {
              detail: { showButton: isMainMap },
            })
          );
        }
      };

      openBuyModalButtonHtml = (
        <button
          type="button"
          className="btn btn-danger flex-fill class_BuyTohonModal"
          style={{ marginTop: "10px" }}
          disabled={tohonBuyList.length < 1}
          onClick={handleOpenBuyModal}
        >
          購入画面を開く
          <br />（{tohonBuyList.length}）
        </button>
      );
    }
  }

  return (
    <li className="item">
      <button
        type="button"
        className={`item__head gsi--info--open ${isOpen ? "active" : ""}`}
        onClick={handleToggle}
        aria-expanded={isOpen}
      >
        {label}
      </button>
      <Collapse in={isOpen}>
        <div className="item__dtl">
          <table width="100%" border="0" className="c-tbl ft--S item__dtl__tbl">
            <tbody>
              {Object.entries(properties)
                .filter(([key]) => !EXCLUDED_PROPERTY_KEYS.includes(key))
                .map(([key, value]) => {
                  let parsedValue;
                  try {
                    if (typeof value === "string") {
                      parsedValue = JSON.parse(value);
                    }
                  } catch (e) {
                    /* パース失敗時は何もしない */
                  }

                  const data =
                    parsedValue && Array.isArray(parsedValue)
                      ? parsedValue
                      : Array.isArray(value)
                      ? value
                      : null;

                  return (
                    <tr key={key}>
                      <th>{key}</th>
                      <td>
                        {data ? (
                          <NestedTables data={data} />
                        ) : key === "ファイル名" && isMainMap && downloadGeojson ? (
                          <>
                            {value} {downloadIcon}
                          </>
                        ) : (
                          value ?? ""
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </Collapse>
      {orderDateHtml}
      {(tohonButtonHtml || openBuyModalButtonHtml) && (
        <div className="d-flex">
          {tohonButtonHtml}
          {openBuyModalButtonHtml}
        </div>
      )}
    </li>
  );
};

/**
 * 任意座標系重ね表示（β版）の注意書き
 * 右スライダーの属性一覧の先頭に1回だけ表示する
 */
export const NiniBetaNote = () => {
  // 「MAPPLE法務局地図ビューア」についてモーダルの該当の章を開く
  const handleOpenDetail = (e) => {
    e.preventDefault();
    window.dispatchEvent(
      new CustomEvent("showAboutViewerModal", {
        detail: { section: ABOUT_SECTION_NINI },
      })
    );
  };

  return (
    <div className="nini-beta-note">
      <span className="nini-beta-note__title">
        {NINI_BETA_NOTICE.PANEL_TITLE}
      </span>
      {NINI_BETA_NOTICE.ONE_LINER}
      <span className="nini-beta-note__more">
        {NINI_BETA_NOTICE.LINK_PREFIX}
        <a href="#_" onClick={handleOpenDetail}>
          {NINI_BETA_NOTICE.LINK_TEXT}
        </a>
      </span>
    </div>
  );
};

/**
 * 選択された地物の詳細を表示するメインコンポーネント
 */
const FeatureDetail = ({
  feature,
  isMainMap,
  downloadGeojson,
  openChibanPolygonDl,
}) => {
  const { properties } = feature;
  // 仮: PLATEAUの建物かどうかで判定
  const isBuilding =
    properties.hasOwnProperty("measuredHeight") ||
    properties.hasOwnProperty("yearOfConstruction");

  return isBuilding ? (
    <BuildingDetail properties={properties} />
  ) : (
    <LandDetail
      properties={properties}
      isMainMap={isMainMap}
      downloadGeojson={downloadGeojson}
      openChibanPolygonDl={openChibanPolygonDl}
    />
  );
};

export default FeatureDetail;
